import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "app/store"
import {
  FeeChargeTypes,
  INCOMPLETE,
  NOT_STARTED,
} from "pages/listing-details-with-step/src/common"
import { policyTypeArray } from "pages/listing-details-with-step/src/review-and-publish-listing/policies-and-rules/cancellation-policy/policy-type-array"
import { apiClient } from "services/client"
import { PDPType } from "services/common"
import { GetPropertyRequest } from "services/properties"
import { FetchRboPropertyDataRequest } from "services/rbo/fetch-rbo-property-api"
import { ListingDataType } from "services/rbo/import-listing"
import { UpdateLocationAddressType } from "services/rbo/location-details-api"
import {
  AvailabilityType,
  CancellationPolicy,
  GetOnboardingStatusDataRequest,
  PricingType,
  PropertyDamageType,
} from "services/rbo/onboarding-status-data"
import {
  GetCancellationPolicyRequest,
  PolicyType,
} from "services/rbo/review-and-publish"
import { RBOInitialState } from "store/rbo/types"

const initialState: RBOInitialState = {
  loading: false,
  error: null,
  currentAddress: null, // location details screen [address form]
  currentPricingValue: null, // pricing screen
  availabilityValue: null, // availability tab value
  pdpPlan: "", // Property Damage Protection screen , Policies & Rules tab [review & publish screen ]
  cancellationPolicyData: policyTypeArray[1], // Policies & Rules tab [review & publish screen ]
  currentMinimumAgeValue: "18", // Minimum Age Requirement screen
  currentImportCalendarLink: "", // Calendar Sync
  selectedDatesList: [], // Calendar tab [review & publish screen ]
  selectedBlockedDatesList: [], // based on whimstay_availability
  selectedListing: undefined,
  getPropertiesData: undefined,
  getRboPropertyData: undefined, // pricing tab & availability tab [review & publish screen ]
  getOnboardingStatusData: undefined, // get onboarding data status
  nextListingUnitId: "", // set next listing id after completed all steps
  isStepAccordionOpen: false, // set to step accordion open or not in calendar sync
  selectedDateOfMonth: null, // set to 'selected date of month' in calendar sync screen
}

export const getPDPPlan = createAsyncThunk(
  "/rbo/pdp",
  async (params: GetCancellationPolicyRequest) => {
    const response = await apiClient.getPdpData(params)

    return response?.data?.data
  },
)

export const getCancellationPolicy = createAsyncThunk(
  "/rbo/cancellation-policy",
  async (params: GetCancellationPolicyRequest) => {
    const response = await apiClient.getCancellationPolicy(params)

    return response?.data?.data
  },
)

export const getPropertyDetailsApi = createAsyncThunk(
  "/rbo/property-data",
  async (params: GetPropertyRequest) => {
    const response = await apiClient.getProperty(params)
    return response?.data?.data
  },
)

export const getRboPropertyDataApi = createAsyncThunk(
  "/rbo/fetch-rbo-property-data",
  async (params: FetchRboPropertyDataRequest) => {
    const response = await apiClient.fetchRboPropertyData(params)

    return response?.data?.data
  },
)

export const getOnBoardingStatusDataApi = createAsyncThunk(
  "/rbo/onboarding-data",
  async (params: GetOnboardingStatusDataRequest) => {
    const response = await apiClient.getOnboardingStatusData(params)
    return response?.data?.data
  },
)

export const rboSlice = createSlice({
  name: "rboSlice",
  initialState,
  reducers: {
    setIsCurrentAddress: (
      state,
      action: PayloadAction<UpdateLocationAddressType>,
    ) => {
      state.currentAddress = action.payload
    },
    setIsCurrentPricingValue: (
      state,
      action: PayloadAction<PricingType | null>,
    ) => {
      state.currentPricingValue = action.payload
    },
    setIsAvailabilityValue: (
      state,
      action: PayloadAction<AvailabilityType>,
    ) => {
      state.availabilityValue = action.payload
    },
    setIsPdpPlan: (state, action: PayloadAction<string>) => {
      state.pdpPlan = action.payload
    },
    setIsCancellationPolicy: (
      state,
      action: PayloadAction<PolicyType | null>,
    ) => {
      state.cancellationPolicyData = action.payload
    },
    setIsMinimumAgeValue: (state, action: PayloadAction<string>) => {
      state.currentMinimumAgeValue = action.payload
    },
    setIsImportCalendarLink: (state, action: PayloadAction<string>) => {
      state.currentImportCalendarLink = action.payload
    },
    setCalenderDatesList: (state, action: PayloadAction<string[]>) => {
      state.selectedDatesList = action.payload
    },
    setCalenderBlockedDatesList: (state, action: PayloadAction<string[]>) => {
      state.selectedBlockedDatesList = action.payload
    },
    setIsSelectedListing: (
      state,
      action: PayloadAction<ListingDataType | undefined>,
    ) => {
      state.selectedListing = action.payload
    },
    setIsNextListingUnitId: (state, action: PayloadAction<string>) => {
      state.nextListingUnitId = action.payload
    },
    setIsGetOnboardingStatusData: (state) => {
      state.getOnboardingStatusData = undefined
    },
    setIsStepAccordionOpen: (state, action: PayloadAction<boolean>) => {
      state.isStepAccordionOpen = action.payload
    },
    setIsSelectedDateOfMonth: (state, action: PayloadAction<number | null>) => {
      state.selectedDateOfMonth = action.payload
    },
    setIsGetPropertiesData: (state) => {
      state.getPropertiesData = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPDPPlan.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getPDPPlan.fulfilled, (state, action) => {
      state.loading = false
      if (action.payload?.isMyOwnPlan) {
        state.pdpPlan = PDPType.Existing
      } else if (action.payload?.isWhimstayPlan) {
        state.pdpPlan = PDPType.Whimstay
      } else {
        state.pdpPlan = ""
      }
    })
    builder.addCase(getPDPPlan.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })

    // ----------- cancellation policy -----------------
    builder.addCase(getCancellationPolicy.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getCancellationPolicy.fulfilled, (state, action) => {
      state.loading = false
      state.cancellationPolicyData = action.payload?.policy[0]
    })
    builder.addCase(getCancellationPolicy.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    // ----------- get property data -----------------
    builder.addCase(getPropertyDetailsApi.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getPropertyDetailsApi.fulfilled, (state, action) => {
      state.loading = false
      state.getPropertiesData = action.payload
    })
    builder.addCase(getPropertyDetailsApi.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    // ----------- fetch rbo property data -----------------
    builder.addCase(getRboPropertyDataApi.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRboPropertyDataApi.fulfilled, (state, action) => {
      state.loading = false
      state.getRboPropertyData = action.payload
    })
    builder.addCase(getRboPropertyDataApi.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    // ----------- fetch rbo onboarding status data -----------------
    builder.addCase(getOnBoardingStatusDataApi.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getOnBoardingStatusDataApi.fulfilled, (state, action) => {
      state.loading = false
      state.getOnboardingStatusData = action.payload

      // ---- pricing ----
      // Set initial values for pricing data from onboarding status data
      const pricingOnboardingData: PricingType =
        typeof action.payload?.pricing === "string"
          ? (JSON.parse(action.payload.pricing) as PricingType)
          : action.payload?.pricing || ({} as PricingType)
      const pricingStatus = action.payload?.pricingStatus

      if (pricingStatus === INCOMPLETE || pricingStatus === NOT_STARTED) {
        state.currentPricingValue = {
          basePrice: pricingOnboardingData?.basePrice || 0,
          discount: pricingOnboardingData?.discount || 20,
          extendedDiscount: pricingOnboardingData?.extendedDiscount || 0,
          cleaningFee: pricingOnboardingData?.cleaningFee || 0,
          petFee: pricingOnboardingData?.petFee || 0,
          petCount: pricingOnboardingData?.petCount || 1,
          petFeeType:
            pricingOnboardingData?.petFeeType || FeeChargeTypes.BOOKING_PER_PET,
          tax: pricingOnboardingData?.tax || 0,
          isTaxApplied: pricingOnboardingData?.isTaxApplied ?? true,
          additionalGuestFee: pricingOnboardingData?.additionalGuestFee || 0,
          additionalGuestCount:
            pricingOnboardingData?.additionalGuestCount || 1,
          otherFeeValue: pricingOnboardingData?.otherFeeValue || 0,
          otherFeeType:
            pricingOnboardingData?.otherFeeType || FeeChargeTypes.BOOKING,
        }
      }

      // ------------- availability step --------
      // Set initial values for availability data from onboarding status data
      const availabilityOnboardingData: AvailabilityType =
        typeof action.payload?.availability === "string"
          ? (JSON.parse(action.payload.availability) as AvailabilityType)
          : action.payload?.availability || ({} as AvailabilityType)
      const availabilityStatus = action.payload?.availabilityStatus

      if (
        availabilityStatus === INCOMPLETE ||
        availabilityStatus === NOT_STARTED
      ) {
        state.availabilityValue = {
          minLengthStay: availabilityOnboardingData?.minLengthStay || 1,
          maxLengthStay: availabilityOnboardingData?.maxLengthStay,
        }
      }

      // ----- property damage protection -----
      const propertyDamageOnboardingData: PropertyDamageType =
        typeof action.payload?.propertyDamage === "string"
          ? (JSON.parse(action.payload.propertyDamage) as PropertyDamageType)
          : action.payload?.propertyDamage || ({} as PropertyDamageType)
      const pdpStatus = action.payload?.propertyDamageStatus

      // Only update pdpPlan if status is "INCOMPLETE" or "NOT_STARTED"
      if (pdpStatus === INCOMPLETE || pdpStatus === NOT_STARTED) {
        const isMyOwnPlan =
          propertyDamageOnboardingData?.isMyOwnPlan === true ||
          propertyDamageOnboardingData?.isMyOwnPlan === "true"
        const isWhimstayPlan =
          propertyDamageOnboardingData?.isWhimstayPlan === true ||
          propertyDamageOnboardingData?.isWhimstayPlan === "true"

        if (isMyOwnPlan) {
          state.pdpPlan = PDPType.Existing
        } else if (isWhimstayPlan) {
          state.pdpPlan = PDPType.Whimstay
        } else {
          state.pdpPlan = ""
        }
      }

      // ---- cancellation policy ----
      const cancellationData: CancellationPolicy =
        typeof action.payload?.cancellationPolicy === "string"
          ? (JSON.parse(
              action.payload.cancellationPolicy,
            ) as CancellationPolicy)
          : action.payload?.cancellationPolicy || ({} as CancellationPolicy)
      const cancellationStatus = action.payload?.cancellationStatus

      // Find the first policy where cancellationData has a true value
      const selectedCancellationPolicy = policyTypeArray.find(
        (policy) =>
          (cancellationData as Record<string, boolean>)[policy.id] === true,
      )

      // Extract only the required properties
      const formattedCancellationPolicy = selectedCancellationPolicy
        ? {
            id: selectedCancellationPolicy.id,
            category: selectedCancellationPolicy.category,
            percentage: selectedCancellationPolicy.percentage,
            validFrom: selectedCancellationPolicy.validFrom,
            validTo: selectedCancellationPolicy.validTo,
            description: selectedCancellationPolicy.description,
          }
        : null // If no match found, set it to null

      // Assign to state only if there's a match and status condition is met
      if (
        formattedCancellationPolicy &&
        (cancellationStatus === INCOMPLETE ||
          cancellationStatus === NOT_STARTED)
      ) {
        state.cancellationPolicyData = formattedCancellationPolicy
      }

      // ---- minimum age requirement ----
      const minimumAge = action.payload?.minAgeRequirement
      const minimumAgeStatus = action.payload?.minAgeStatus
      if (minimumAgeStatus === INCOMPLETE || minimumAgeStatus === NOT_STARTED) {
        state.currentMinimumAgeValue = minimumAge || "18"
      }

      // ----- calendar sync -----
      // Parse the stored calendar data if it's a string, otherwise use the object directly
      const importCalendarLink: Record<string, any> =
        typeof action.payload?.calendar === "string"
          ? JSON.parse(action.payload.calendar)
          : action.payload?.calendar || {}
      const calendarStatus = action.payload?.calendarStatus
      if (calendarStatus === INCOMPLETE || calendarStatus === NOT_STARTED) {
        state.currentImportCalendarLink =
          importCalendarLink?.importCalendar || ""
      }
    })

    builder.addCase(getOnBoardingStatusDataApi.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
  },
})

export const {
  setIsCurrentAddress,
  setIsCurrentPricingValue,
  setIsAvailabilityValue,
  setIsPdpPlan,
  setIsCancellationPolicy,
  setIsMinimumAgeValue,
  setIsImportCalendarLink,
  setCalenderDatesList,
  setIsSelectedListing,
  setIsGetOnboardingStatusData,
  setIsNextListingUnitId,
  setIsStepAccordionOpen,
  setIsGetPropertiesData,
  setCalenderBlockedDatesList,
  setIsSelectedDateOfMonth,
} = rboSlice.actions

export const selectCurrentAddress = (state: RootState) =>
  state.rboReducer.currentAddress

export const selectCurrentPricingValue = (state: RootState) =>
  state.rboReducer.currentPricingValue

export const selectAvailabilityValue = (state: RootState) =>
  state.rboReducer.availabilityValue

export const selectPdpPlan = (state: RootState) => state.rboReducer.pdpPlan

export const selectCancellationPolicy = (state: RootState) =>
  state.rboReducer.cancellationPolicyData

export const selectMinimumAgeValue = (state: RootState) =>
  state.rboReducer.currentMinimumAgeValue

export const selectImportCalendarLink = (state: RootState) =>
  state.rboReducer.currentImportCalendarLink

export const selectCalenderDatesList = (state: RootState) =>
  state.rboReducer.selectedDatesList

export const selectCalenderBlockedDatesList = (state: RootState) =>
  state.rboReducer.selectedBlockedDatesList

export const selectSelectedListing = (state: RootState) =>
  state.rboReducer.selectedListing

export const selectPropertiesData = (state: RootState) =>
  state.rboReducer.getPropertiesData

export const selectRboPropertyData = (state: RootState) =>
  state.rboReducer.getRboPropertyData

export const selectOnBoardingStatusData = (state: RootState) =>
  state.rboReducer.getOnboardingStatusData

export const selectNextListingUnitId = (state: RootState) =>
  state.rboReducer.nextListingUnitId

export const selectIsStepAccordionOpen = (state: RootState) =>
  state.rboReducer.isStepAccordionOpen

export const selectedDateOfMonth = (state: RootState) =>
  state.rboReducer.selectedDateOfMonth

export default rboSlice.reducer
